import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { clientAPI } from '../api';
import ImageLightbox from '../components/ImageLightbox';
import { getTranslation } from '../i18n';

const TONE_MAP = {
  'default':    { page: '#faf8f5', section: '#ffffff', border: '#ece9e4', text: '#4a4540', textLight: '#8a847b', accent: '#b8a992' },
  'ivory':      { page: '#faf8f5', section: '#ffffff', border: '#ece9e4', text: '#4a4540', textLight: '#8a847b', accent: '#b8a992' },
  'warm-sand':  { page: '#f5f0e8', section: '#faf7f2', border: '#e8e0d4', text: '#4a4035', textLight: '#8a7e72', accent: '#b09878' },
  'soft-grey':  { page: '#f0f0ee', section: '#f7f7f6', border: '#e2e2df', text: '#3c3c3a', textLight: '#7a7a76', accent: '#9a9a96' },
  'dusty-blue': { page: '#edf1f5', section: '#f5f7fa', border: '#dbe1e8', text: '#3a4048', textLight: '#6e7880', accent: '#8ea0b0' },
};

function deriveCustomTone(hex) {
  if (!hex || hex.length !== 7) return TONE_MAP['default'];
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c, t, w) => Math.round(c * w + t * (1 - w));
  const toHex = (r2, g2, b2) => '#' + [r2, g2, b2].map(c => c.toString(16).padStart(2, '0')).join('');
  return {
    page:      toHex(mix(r, 255, 0.08), mix(g, 255, 0.08), mix(b, 255, 0.08)),
    section:   toHex(mix(r, 255, 0.03), mix(g, 255, 0.03), mix(b, 255, 0.03)),
    border:    toHex(mix(r, 230, 0.12), mix(g, 230, 0.12), mix(b, 230, 0.12)),
    text:      toHex(mix(r, 50, 0.2), mix(g, 50, 0.2), mix(b, 50, 0.2)),
    textLight: toHex(mix(r, 120, 0.2), mix(g, 120, 0.2), mix(b, 120, 0.2)),
    accent:    toHex(mix(r, 160, 0.35), mix(g, 160, 0.35), mix(b, 160, 0.35)),
  };
}

function resolveTone(toneId, customColor) {
  if (toneId === 'custom' && customColor) return deriveCustomTone(customColor);
  return TONE_MAP[toneId] || TONE_MAP['default'];
}

const FMT_DATETIME = (iso, lang = 'en') => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(
      lang === 'pl' ? 'pl-PL' : lang === 'de' ? 'de-DE' : 'en-GB',
      { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' },
    );
  } catch (e) { return ''; }
};

const ApprovalView = () => {
  const { approvalToken } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [confirming, setConfirming] = useState(false);
  const [confirmedAt, setConfirmedAt] = useState(null);
  const [finalCount, setFinalCount] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await clientAPI.openApproval(approvalToken);
      setState({ loading: false, error: null, data: res.data });
      const board = res.data.board || {};
      if (board.final_confirmed_at) {
        setConfirmedAt(board.final_confirmed_at);
        setFinalCount(board.final_confirmed_count || 0);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setState({ loading: false, error: { status, detail }, data: null });
    }
  }, [approvalToken]);

  useEffect(() => { load(); }, [load]);

  // Loading state
  if (state.loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#faf8f5', color: '#8a847b', fontFamily: 'Georgia, serif',
        fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase',
      }}>
        …
      </div>
    );
  }

  // Error / invalid token
  if (state.error || !state.data?.board) {
    const lang = 'en';
    const t = getTranslation(lang);
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#faf8f5', padding: 24,
      }}>
        <div style={{
          maxWidth: 460, padding: '40px 36px', background: '#ffffff', border: '1px solid #ece9e4',
          textAlign: 'center',
        }}>
          <p style={{
            fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
            color: '#b0a89e', margin: '0 0 14px',
          }}>{t.fd_approval_eyebrow}</p>
          <h1 style={{
            fontFamily: 'Playfair Display, serif', fontSize: 22, color: '#4a4540',
            margin: '0 0 14px', lineHeight: 1.35,
          }}>
            {t.fd_approval_link_invalid}
          </h1>
        </div>
      </div>
    );
  }

  const { board, approval_email } = state.data;
  const branding = board.branding || {};
  const tone = resolveTone(branding.atmosphere_tone || 'default', branding.atmosphere_color);
  const lang = (board.client_language || branding.workspace_lang || 'en').toLowerCase();
  const t = getTranslation(lang);

  const workspaceName = branding.workspace_name || 'Maestra';
  const projectName = board.name || '';
  const finals = (board.images || []).filter(i => i.is_final);
  const isLocked = board.is_locked || board.current_status === 'LOCKED';

  const toggleFinal = async (imageId) => {
    if (isLocked) return;
    try {
      const res = await clientAPI.approvalToggleFinal(approvalToken, imageId);
      setState(prev => ({
        ...prev,
        data: {
          ...prev.data,
          board: {
            ...prev.data.board,
            images: prev.data.board.images.map(img =>
              img.id === imageId ? { ...img, is_final: res.data.is_final } : img,
            ),
          },
        },
      }));
    } catch (e) {
      console.warn('toggle failed', e);
    }
  };

  const confirmDirection = async () => {
    if (confirming || isLocked) return;
    setConfirming(true);
    try {
      const res = await clientAPI.approvalConfirm(approvalToken);
      setConfirmedAt(res.data.confirmed_at);
      setFinalCount(res.data.final_count || 0);
    } catch (e) {
      console.warn('confirm failed', e);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: tone.page, color: tone.text, fontFamily: 'Georgia, serif' }}>
      {/* === Brand bar === */}
      <header style={{
        padding: '36px 24px 28px',
        textAlign: 'center',
        borderBottom: `1px solid ${tone.border}`,
      }}>
        {branding.logo_url && branding.branding_display_mode !== 'name_only' && (
          <img
            src={branding.logo_url}
            alt={workspaceName}
            style={{ maxHeight: 48, maxWidth: 180, marginBottom: 10, opacity: 0.92 }}
          />
        )}
        {branding.branding_display_mode !== 'logo_only' && (
          <p style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 18, letterSpacing: '0.18em', margin: 0, color: tone.text,
            textTransform: 'uppercase',
          }}>
            {workspaceName}
          </p>
        )}
      </header>

      {/* === Approval space header === */}
      <section style={{
        maxWidth: 920, margin: '0 auto', padding: '52px 24px 28px', textAlign: 'center',
      }}>
        <p style={{
          fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: tone.textLight, margin: '0 0 16px', display: 'inline-flex',
          alignItems: 'center', gap: 10,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', backgroundColor: tone.accent,
            boxShadow: `0 0 0 4px ${tone.accent}1a`,
          }} />
          {t.fd_approval_eyebrow}
        </p>
        <h1 style={{
          fontFamily: 'Playfair Display, serif', fontSize: 'clamp(28px, 5vw, 44px)',
          fontWeight: 400, lineHeight: 1.2, margin: '0 0 14px', color: tone.text,
        }}>
          {projectName}
        </h1>
        <p style={{
          fontSize: 13, color: tone.textLight, margin: '0 0 6px',
          letterSpacing: '0.04em',
        }}>
          {t.fd_approval_signed_in_as} <strong style={{ color: tone.text, fontWeight: 500 }}>{approval_email}</strong>
        </p>
        <p style={{
          fontSize: 14, color: tone.textLight, margin: '20px auto 0',
          maxWidth: 540, lineHeight: 1.7, fontStyle: 'italic',
        }}>
          {t.fd_approval_intro}
        </p>
      </section>

      {/* === Image gallery === */}
      <section style={{
        maxWidth: 1120, margin: '0 auto', padding: '24px 24px 60px',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 18,
        }}>
          {(board.images || []).map((img) => {
            const isFinal = !!img.is_final;
            return (
              <div
                key={img.id}
                data-testid={`approval-card-${img.id}`}
                style={{
                  background: tone.section,
                  border: `1px solid ${tone.border}`,
                  boxShadow: isFinal ? `0 0 0 2px ${tone.accent}` : 'none',
                  transition: 'box-shadow 0.25s ease',
                  display: 'flex', flexDirection: 'column',
                }}
              >
                <button
                  type="button"
                  onClick={() => setLightboxImage(img)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'zoom-in',
                    display: 'block',
                  }}
                >
                  <img
                    src={img.image_thumb || img.image_data}
                    alt={img.filename || ''}
                    style={{ width: '100%', height: 280, objectFit: 'cover', display: 'block' }}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => toggleFinal(img.id)}
                  disabled={isLocked}
                  data-testid={`approval-final-toggle-${img.id}`}
                  style={{
                    margin: 0,
                    padding: '11px 12px',
                    background: isFinal ? `${tone.accent}14` : 'transparent',
                    border: 'none',
                    borderTop: `1px solid ${tone.border}`,
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: isFinal ? tone.accent : tone.textLight,
                    cursor: isLocked ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    fontFamily: 'Georgia, serif',
                  }}
                >
                  <span style={{ fontSize: 14, lineHeight: 1 }}>{isFinal ? '★' : '☆'}</span>
                  {isFinal ? t.fd_unmark_final : t.fd_mark_final}
                </button>
              </div>
            );
          })}
        </div>

        {/* === Confirm row === */}
        <div style={{
          marginTop: 56, paddingTop: 32, borderTop: `1px solid ${tone.border}`,
          textAlign: 'center',
        }}>
          {confirmedAt && (
            <p
              data-testid="fd-confirmed-banner"
              style={{
                fontSize: 11,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: tone.accent,
                margin: '0 0 22px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                backgroundColor: tone.accent,
                boxShadow: `0 0 0 4px ${tone.accent}1a`,
              }} />
              {t.fd_audit_confirmed} · {FMT_DATETIME(confirmedAt, lang)}
            </p>
          )}
          {finals.length > 0 && (
            <p
              data-testid="fd-change-hint"
              style={{
                fontSize: 12,
                color: tone.textLight,
                fontStyle: 'italic',
                margin: '0 auto 22px',
                maxWidth: 480,
                lineHeight: 1.7,
              }}
            >
              {t.fd_approval_change_hint}
            </p>
          )}
          <button
            type="button"
            onClick={confirmDirection}
            disabled={confirming || isLocked || finals.length === 0}
            data-testid="fd-confirm-cta"
            style={{
              background: tone.text, color: tone.section,
              border: 'none', padding: '15px 38px',
              fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase',
              cursor: finals.length === 0 ? 'not-allowed' : 'pointer',
              fontFamily: 'Georgia, serif',
              opacity: finals.length === 0 ? 0.5 : 1,
              transition: 'opacity 0.2s, transform 0.2s',
            }}
          >
            {confirming ? '…' : (confirmedAt ? t.fd_approval_reconfirm_cta : t.fd_approval_confirm_cta)}
          </button>
        </div>
      </section>

      {/* === Footer === */}
      <footer style={{
        padding: '28px 24px',
        borderTop: `1px solid ${tone.border}`,
        textAlign: 'center',
        color: tone.textLight,
        fontSize: 11, letterSpacing: '0.06em',
      }}>
        {workspaceName} · {new Date().getFullYear()}
      </footer>

      {lightboxImage && (
        <ImageLightbox
          image={lightboxImage}
          images={board.images || []}
          currentIndex={(board.images || []).findIndex(i => i.id === lightboxImage.id)}
          onClose={() => setLightboxImage(null)}
          onNavigate={(img) => setLightboxImage(img)}
        />
      )}
    </div>
  );
};

export default ApprovalView;
